import {useEffect, useMemo, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    TablePagination,
    Tooltip,
    CircularProgress,
    Box,
    Typography,
} from "@mui/material";
import {apiGet} from "../utils";

const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");
const formatDays = (v) => (v == null ? "-" : `${v}`);
const formatGapDays = (v) => (v == null ? "-" : `${v}`);
const formatRatio = (v) => (v == null ? "-" : `${v}x`);
const NO_CADENCE_NOTE = "Not enough order history (fewer than 2 orders in the trailing 12 months) to establish a typical order gap. Flagged overdue because it's been 90+ days since the last order instead.";

const COLUMNS = [
    {key: "account_name", label: "Account", numeric: false},
    {key: "parent_account_name", label: "Parent Account", numeric: false},
    {key: "last_order_date", label: "Last Order Date", numeric: false},
    {key: "days_since_last_order", label: "Days Since Last Order", numeric: true},
    {key: "avg_gap_days", label: "Typical Gap (days)", numeric: true},
    {key: "gap_ratio", label: "X Usual", numeric: true},
];

const sortRows = (rows, orderBy, order) => {
    if (!orderBy) {
        return rows;
    }
    const direction = order === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === "string") {
            return aVal.localeCompare(bVal) * direction;
        }
        return (aVal - bVal) * direction;
    });
};

export const OverdueAccountsModal = ({open, onClose}) => {
    const [loading, setLoading] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [orderBy, setOrderBy] = useState("gap_ratio");
    const [order, setOrder] = useState("desc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        if (!open) {
            return;
        }
        setLoading(true);
        setAccounts([]);
        setPage(0);
        apiGet("v2/agent/sales-performance/overdue-accounts", (resp) => {
            setAccounts(resp?.success ? (resp.data?.accounts || []) : []);
            setLoading(false);
        });
    }, [open]);

    const sortedAccounts = useMemo(() => sortRows(accounts, orderBy, order), [accounts, orderBy, order]);
    const pagedAccounts = useMemo(
        () => sortedAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedAccounts, page, rowsPerPage]
    );

    const handleSort = (key) => {
        if (orderBy === key) {
            setOrder(order === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(key);
            setOrder("asc");
        }
        setPage(0);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Overdue Accounts</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", p: 3}}>
                        <CircularProgress size={24}/>
                    </Box>
                ) : (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 1}}>
                            {accounts.length} accounts total. "n/a" in Typical Gap / X Usual means there isn't
                            enough order history to establish a cadence for that account - hover for details.
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        {COLUMNS.map((col) => (
                                            <TableCell key={col.key} align={col.numeric ? "right" : "left"}>
                                                <TableSortLabel
                                                    active={orderBy === col.key}
                                                    direction={orderBy === col.key ? order : "asc"}
                                                    onClick={() => handleSort(col.key)}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagedAccounts.map((a, i) => (
                                        <TableRow key={a.account_id}>
                                            <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                                            <TableCell>{a.account_name}</TableCell>
                                            <TableCell>{a.parent_account_name !== a.account_name ? a.parent_account_name : "-"}</TableCell>
                                            <TableCell>{formatDate(a.last_order_date)}</TableCell>
                                            <TableCell align="right">{formatDays(a.days_since_last_order)}</TableCell>
                                            <TableCell align="right">
                                                {a.cadence_established ? formatGapDays(a.avg_gap_days) : (
                                                    <Tooltip title={NO_CADENCE_NOTE}>
                                                        <span>n/a</span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {a.cadence_established ? formatRatio(a.gap_ratio) : (
                                                    <Tooltip title={NO_CADENCE_NOTE}>
                                                        <span>n/a</span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>{a.stopped ? "Stopped" : "Overdue"}</TableCell>
                                        </TableRow>
                                    ))}
                                    {!pagedAccounts.length && (
                                        <TableRow>
                                            <TableCell colSpan={COLUMNS.length + 2}>No overdue accounts found.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {accounts.length > 0 && (
                            <TablePagination
                                component="div"
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                count={accounts.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
